import { Injectable, signal, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap, timeout, catchError, of } from 'rxjs';
import { ServiceSystemTopbarDate } from './system-date.topbar.service';
import { Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  DATA_MODULO_PARAMETROS,
  MODULO_PARAMETROS_POR_DEFECTO,
  ModuloParametros,
  ParametrosSistemaPort,
} from './parametros-sistema.port';

export interface SystemDateState {
  fechaSistema: string | null;
  isLoading: boolean;
  lastUpdate: Date | null;
  module: ModuloParametros | null;
}

/** Cada cuanto se refresca la fecha sin cambiar de modulo. */
const VIGENCIA_MS = 300000;

/**
 * Fecha de operacion que muestra la barra superior.
 *
 * Es una fachada: no sabe a que microservicio preguntar. Cada libreria registra
 * su servicio de parametros con `registrarFuente()` al arrancar, y la ruta
 * declara de que modulo son sus parametros con `data.parametrosModulo`. La
 * pantalla que no lo declare hereda el de su ruta padre, y si nadie lo declara
 * se leen los de contabilidad, que es el modulo por defecto.
 */
@Injectable({ providedIn: 'root' })
export class SystemSyncTopbarDateService {
  private readonly fuentePorDefecto = inject(ServiceSystemTopbarDate);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly STORAGE_KEY = 'system_date_state_topbar';

  /** Servicio de parametros de cada libreria que se haya presentado. */
  private readonly fuentes = new Map<ModuloParametros, ParametrosSistemaPort>();

  private readonly fechaSistemaSignal = signal<string | null>(null);
  private readonly isLoadingSignal = signal<boolean>(false);
  private readonly lastUpdateSignal = signal<Date | null>(null);
  private readonly moduleSignal = signal<ModuloParametros | null>(null);
  private readonly isLoadedSignal = signal<boolean>(false);

  readonly fechaSistema = this.fechaSistemaSignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly lastUpdate = this.lastUpdateSignal.asReadonly();
  readonly currentModule = this.moduleSignal.asReadonly();
  readonly isLoaded = this.isLoadedSignal.asReadonly();

  readonly fechaSistemaFormatted = computed(() => {
    const fecha = this.fechaSistemaSignal();
    return fecha ? this.formatDate(fecha) : '';
  });

  constructor() {
    this.restoreFromLocalStorage();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.sincronizarConRutaActiva());
  }

  /**
   * Presenta el servicio de parametros de una libreria.
   *
   * Lo llama cada modulo al arrancar. Mientras no lo haga, sus pantallas leen
   * la fuente por defecto.
   */
  registrarFuente(modulo: ModuloParametros, fuente: ParametrosSistemaPort): void {
    this.fuentes.set(modulo, fuente);

    // Si la pantalla en la que ya estamos es de este modulo, su fecha estaba
    // saliendo de la fuente por defecto: se pide de nuevo a quien corresponde.
    if (this.moduloDeLaRutaActiva() === modulo) {
      this.loadSystemDate(true);
    }
  }

  /** Modulo declarado por la ruta activa, o el de por defecto. */
  private moduloDeLaRutaActiva(): ModuloParametros {
    let nodo: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let declarado: ModuloParametros | null = null;

    // La hoja manda: se recorre hasta el final y gana la ultima declaracion,
    // de modo que una pantalla puede apartarse del modulo de su libreria.
    while (nodo) {
      const enRuta = nodo.data?.[DATA_MODULO_PARAMETROS] as ModuloParametros | undefined;
      if (enRuta) {
        declarado = enRuta;
      }
      nodo = nodo.firstChild;
    }

    return declarado ?? MODULO_PARAMETROS_POR_DEFECTO;
  }

  /** Servicio al que toca preguntar: el del modulo activo o la fuente por defecto. */
  private fuenteActiva(): ParametrosSistemaPort {
    return this.fuentes.get(this.moduloDeLaRutaActiva()) ?? this.fuentePorDefecto;
  }

  /** Pide la fecha cuando cambia el modulo o cuando la que hay ya caduco. */
  private sincronizarConRutaActiva(): void {
    const modulo = this.moduloDeLaRutaActiva();

    if (this.moduleSignal() !== modulo) {
      this.clearCache();
      this.loadSystemDate(true);
      return;
    }

    const lastUpdate = this.lastUpdateSignal();
    const caducada = !lastUpdate || new Date().getTime() - lastUpdate.getTime() > VIGENCIA_MS;
    if (!this.fechaSistemaSignal() || caducada) {
      this.loadSystemDate();
    }
  }

  public restoreFromLocalStorage(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (!saved) return;

      const state: SystemDateState = JSON.parse(saved);
      // Solo sirve si es del modulo en el que se esta entrando: al arrancar en
      // otro modulo se pintaba la fecha de la sesion anterior.
      if (state.fechaSistema && state.module === this.moduloDeLaRutaActiva()) {
        this.fechaSistemaSignal.set(state.fechaSistema);
        this.lastUpdateSignal.set(state.lastUpdate ? new Date(state.lastUpdate) : null);
        this.moduleSignal.set(state.module);
        this.isLoadedSignal.set(true);
      }
    } catch {
      // Un estado guardado ilegible no debe impedir arrancar: se pide de nuevo.
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private saveToLocalStorage(): void {
    const state: SystemDateState = {
      fechaSistema: this.fechaSistemaSignal(),
      isLoading: this.isLoadingSignal(),
      lastUpdate: this.lastUpdateSignal(),
      module: this.moduleSignal(),
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  loadSystemDate(forceRefresh = false): void {
    if (!forceRefresh && this.isLoadedSignal()) return;
    if (this.isLoadingSignal()) return;

    const modulo = this.moduloDeLaRutaActiva();
    this.isLoadingSignal.set(true);

    this.fuenteActiva()
      .getSystemDate()
      .pipe(
        timeout(10000),
        tap({
          next: (params) => {
            if (params?.fechaSistema) {
              this.fechaSistemaSignal.set(params.fechaSistema);
              this.moduleSignal.set(modulo);
              this.lastUpdateSignal.set(new Date());
              this.isLoadedSignal.set(true);
              this.saveToLocalStorage();
            } else {
              this.handleNoDateResponse();
            }
          },
          error: () => this.handleNoDateResponse(),
        }),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.isLoadingSignal.set(false),
        error: () => this.isLoadingSignal.set(false),
      });
  }

  /**
   * Sin fecha del modulo se conserva la ultima conocida.
   *
   * No se inventa la fecha del equipo: la barra anuncia la fecha de OPERACION,
   * que puede ir por detras del calendario, y ponerla del equipo daba por buena
   * una fecha que el sistema no reconoce.
   */
  private handleNoDateResponse(): void {
    if (this.fechaSistemaSignal()) {
      this.lastUpdateSignal.set(new Date());
    }
    this.isLoadedSignal.set(false);
    this.saveToLocalStorage();
  }

  updateSystemDateFromParameters(newDate: string | null): void {
    if (newDate) {
      this.fechaSistemaSignal.set(newDate);
      this.lastUpdateSignal.set(new Date());
      this.isLoadedSignal.set(true);
      this.saveToLocalStorage();
    } else {
      this.clearCache();
      this.loadSystemDate(true);
    }
  }

  loadSystemDateForCompany(_companyId: string): void {
    this.clearCache();
    this.loadSystemDate(true);
  }

  updateSystemDate(newDate: string): void {
    this.fechaSistemaSignal.set(newDate);
    this.lastUpdateSignal.set(new Date());
    this.isLoadedSignal.set(true);
    this.saveToLocalStorage();
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';

    try {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  clearCache(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.fechaSistemaSignal.set(null);
    this.lastUpdateSignal.set(null);
    this.moduleSignal.set(null);
    this.isLoadedSignal.set(false);
  }

  refresh(): void {
    this.clearCache();
    this.loadSystemDate(true);
  }

  hasCachedData(): boolean {
    return this.fechaSistemaSignal() !== null && this.isLoadedSignal();
  }
}
